/**
 * Frota · Manutenção · Não conformidades.
 *
 * Lista rastreável: cada ocorrência aponta para o checklist que a gerou, para o
 * item, para o veículo e para o motorista. É daqui que a empresa decide abrir a
 * ordem de serviço — nenhuma O.S. nasce sozinha (a escada criticidade→O.S. está
 * PENDENTE DE VALIDAÇÃO com o cliente).
 */
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { MaintenanceNonConformityDto } from "@bi/types";
import { api, ApiError } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { EmptyState } from "../components/EmptyState";
import { IconWrench } from "../components/icons";
import { CriticalityBadge, NC_STATUS, WO_STATUS } from "../features/maintenance/labels";
import { EvidenceThumbs } from "../features/maintenance/EvidenceThumbs";

export function MaintenanceNonConformitiesPage() {
  const [params, setParams] = useSearchParams();
  const qc = useQueryClient();
  const [erro, setErro] = useState<string | null>(null);

  const plate = params.get("plate") ?? "";
  const status = params.get("status") ?? "";
  const criticality = params.get("criticality") ?? "";

  const lista = useQuery({
    queryKey: ["maintenance", "ncs", plate, status, criticality],
    queryFn: () => {
      const q = new URLSearchParams();
      if (plate) q.set("plate", plate);
      if (status) q.set("status", status);
      if (criticality) q.set("criticality", criticality);
      q.set("limit", "100");
      return api.get<{ rows: MaintenanceNonConformityDto[]; total: number }>(`/maintenance/non-conformities?${q}`);
    },
  });

  const mudarStatus = useMutation({
    mutationFn: ({ id, novo }: { id: string; novo: string }) =>
      api.patch(`/maintenance/non-conformities/${id}`, { status: novo }),
    onSuccess: () => { setErro(null); qc.invalidateQueries({ queryKey: ["maintenance"] }); },
    onError: (e) => setErro(e instanceof ApiError ? e.message : "Não foi possível mudar a situação."),
  });

  const abrirOS = useMutation({
    mutationFn: (nonConformityId: string) => api.post("/maintenance/work-orders", { nonConformityId }),
    onSuccess: () => { setErro(null); qc.invalidateQueries({ queryKey: ["maintenance"] }); },
    onError: (e) => setErro(e instanceof ApiError ? e.message : "Não foi possível abrir a ordem de serviço."),
  });

  const setParam = (chave: string, valor: string) => {
    const p = new URLSearchParams(params);
    if (valor) p.set(chave, valor); else p.delete(chave);
    setParams(p, { replace: true });
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Frota · Manutenção · Não conformidades"
        subtitle="Ocorrências abertas pelos checklists, com origem rastreável até o item"
        actions={
          <div className="flex gap-2">
            <Link className="btn-secondary" to="/frota/manutencao/checklists">Checklists</Link>
            <Link className="btn-secondary" to="/frota/manutencao/ordens">Ordens de serviço</Link>
          </div>
        }
      />

      <div className="card p-4 flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-xs text-gray-500">Placa</span>
          <input className="input w-40 uppercase font-mono" defaultValue={plate}
            onBlur={(e) => setParam("plate", e.target.value.trim().toUpperCase())} />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-gray-500">Situação</span>
          <select className="input w-40" value={status} onChange={(e) => setParam("status", e.target.value)}>
            <option value="">Todas</option>
            <option value="open">Aberta</option>
            <option value="in_analysis">Em análise</option>
            <option value="resolved">Resolvida</option>
            <option value="cancelled">Cancelada</option>
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-gray-500">Criticidade</span>
          <select className="input w-36" value={criticality} onChange={(e) => setParam("criticality", e.target.value)}>
            <option value="">Todas</option>
            <option value="high">Alta</option>
            <option value="medium">Média</option>
            <option value="low">Baixa</option>
          </select>
        </label>
        {(plate || status || criticality) && (
          <button className="btn-secondary" onClick={() => setParams({}, { replace: true })}>Limpar</button>
        )}
      </div>

      {erro && <div className="card p-3 border-danger-600/30 bg-danger-50 text-sm text-danger-600">{erro}</div>}

      <div className="card overflow-hidden">
        {lista.isLoading ? (
          <div className="p-8 text-sm text-gray-500">Carregando…</div>
        ) : (lista.data?.rows.length ?? 0) === 0 ? (
          <EmptyState
            icon={<IconWrench className="w-6 h-6" />}
            title="Nenhuma não conformidade"
            description="Ocorrências aparecem aqui assim que um checklist com item não conforme é concluído."
          />
        ) : (
          <div className="divide-y divide-gray-100">
            {lista.data!.rows.map((nc) => (
              <div key={nc.id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-gray-900">{nc.itemLabel}{nc.defectLabel ? ` — ${nc.defectLabel}` : ""}</span>
                    <CriticalityBadge value={nc.criticality} />
                    <span className={NC_STATUS[nc.status].badge}>{NC_STATUS[nc.status].label}</span>
                    {nc.workOrder && (
                      <Link to={`/frota/manutencao/ordens/${nc.workOrder.id}`} className={`${WO_STATUS[nc.workOrder.status].badge} hover:underline`}>
                        O.S. #{nc.workOrder.number}
                      </Link>
                    )}
                  </div>
                  <div className="text-sm text-gray-500 mt-1">
                    <span className="font-mono">{nc.vehiclePlate}</span>
                    {nc.trailerPlate && <span className="font-mono"> + {nc.trailerPlate}</span>}
                    {nc.driverName && <> · {nc.driverName}</>}
                    {nc.inspectorName && <> · aplicado por {nc.inspectorName}</>}
                    {" · "}
                    {new Date(nc.openedAt).toLocaleString("pt-BR")}
                    {" · "}
                    <Link className="text-tm-600 hover:underline" to={`/frota/manutencao/checklists/${nc.runId}`}>
                      ver checklist
                    </Link>
                  </div>
                  {nc.description && <p className="text-sm text-gray-700 mt-1">{nc.description}</p>}
                  {nc.tire && (
                    <p className="text-xs text-gray-700 mt-0.5">
                      Pneu <Link className="font-mono text-tm-700 hover:underline" to={`/frota/pneus/${nc.tire.id}`}>{nc.tire.code}</Link>
                      {nc.tirePosition ? ` · ${nc.tirePosition.label}` : ""}
                    </p>
                  )}
                  <EvidenceThumbs anexos={nc.attachments} size="sm" />
                </div>

                <div className="flex gap-2 flex-shrink-0">
                  {nc.status === "open" && (
                    <button className="btn-secondary" onClick={() => mudarStatus.mutate({ id: nc.id, novo: "in_analysis" })}>
                      Em análise
                    </button>
                  )}
                  {(nc.status === "open" || nc.status === "in_analysis") && (
                    <button className="btn-secondary" onClick={() => mudarStatus.mutate({ id: nc.id, novo: "resolved" })}>
                      Resolver
                    </button>
                  )}
                  {!nc.workOrderId && nc.status !== "cancelled" && nc.status !== "resolved" && (
                    <button className="btn-primary" onClick={() => abrirOS.mutate(nc.id)}>
                      Abrir O.S.
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
